// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { Shipment, shipmentSchema } from '@/shipment/api/types';
import { z } from 'zod';

/**
 * Schema for validating the response from the cancel shipment API endpoint.
 */
const cancelShipmentResponseSchema = z.object({
  data: shipmentSchema,
  metadata: responseMetadataSchema,
});

interface CancelShipmentRequest {
  cancellationReason: string;
  removeLinkedShippingCost?: boolean;
}

/**
 * Cancels an existing shipment with the specified reason.
 * Only shipments in PENDING status can be cancelled.
 *
 * @param shipmentKey - The key of the shipment to cancel
 * @param request - The cancellation details
 * @returns The cancelled shipment
 */
export async function cancelShipment(
  shipmentKey: string,
  request: CancelShipmentRequest
): Promise<Shipment> {
  const url = `/v1/shipment/${shipmentKey}/cancel`;
  const response = await api.post(url, request);

  const { data } = await parseResponse(
    cancelShipmentResponseSchema,
    response.data,
    {
      schemaName: 'CancelShipment',
      endpoint: url,
    }
  );
  return data;
}
